import { useCallback, useEffect, useState } from 'react';

const SYSTEM_COLOR_SETTING = '(prefers-color-scheme: dark)';

type ColorScheme = 'light' | 'dark';
type ColorSchemePreference = ColorScheme | 'system';

export function useColorScheme() {
	const [useSystemPreference, setUseSystemPreference] = useState(true);
	const [activeColorScheme, setActiveColorScheme] = useState<ColorScheme>('light');

	useEffect(() => {
		if (typeof document === 'undefined') {
			setActiveColorScheme('light');
			setUseSystemPreference(true);
		} else {
			setActiveColorScheme(
				document.documentElement.classList.contains('dark') || localStorage['color-scheme'] === 'dark'
					? 'dark'
					: 'light',
			);
			setUseSystemPreference(!('color-scheme' in window.localStorage));
		}
	}, []);

	const onSystemChange = useCallback(
		(event: MediaQueryListEvent) => {
			if (useSystemPreference) {
				const colorScheme = event.matches ? 'dark' : 'light';
				setActiveColorScheme(colorScheme);
			}
		},
		[useSystemPreference, setActiveColorScheme],
	);

	useEffect(() => {
		const matchMedia = window.matchMedia(SYSTEM_COLOR_SETTING);
		matchMedia.addEventListener('change', onSystemChange, false);

		return function cleanup() {
			matchMedia.removeEventListener('change', onSystemChange);
		};
	}, [onSystemChange]);

	// Listen for changes in the class attribute
	useEffect(() => {
		const callback = function (mutationsList: MutationRecord[]) {
			for (const mutation of mutationsList) {
				if (mutation.attributeName === 'class') {
					setActiveColorScheme(document.documentElement.classList.contains('dark') ? 'dark' : 'light');
				}
			}
		};
		const observer = new MutationObserver(callback);

		observer.observe(document.documentElement, { attributes: true });

		return function cleanup() {
			observer.disconnect();
		};
	}, []);

	const handleSystemPreferenceChange = useCallback(() => {
		if (useSystemPreference) {
			// stop using system
			localStorage['color-scheme'] = activeColorScheme === 'dark' ? 'dark' : 'light';
		} else {
			// start using system
			localStorage.removeItem('color-scheme');
			setActiveColorScheme(window.matchMedia(SYSTEM_COLOR_SETTING).matches ? 'dark' : 'light');

			if (window.matchMedia(SYSTEM_COLOR_SETTING).matches) {
				document.documentElement.classList.add('dark');
				document.documentElement.style.colorScheme = 'dark';
			} else {
				document.documentElement.classList.remove('dark');
				document.documentElement.style.colorScheme = 'light';
			}
		}
		setUseSystemPreference(!useSystemPreference);
	}, [activeColorScheme, setActiveColorScheme, useSystemPreference, setUseSystemPreference]);

	const setColorScheme = useCallback((scheme: ColorScheme) => {
		setUseSystemPreference(false);

		document.documentElement.classList.remove(scheme === 'dark' ? 'light' : 'dark');
		document.documentElement.classList.add(scheme);
		document.documentElement.style.colorScheme = scheme;

		localStorage['color-scheme'] = scheme;
		setActiveColorScheme(scheme);
	}, []);

	const setColorSchemePreference = useCallback(
		(preference: ColorSchemePreference) => {
			switch (preference) {
				case 'dark':
				case 'light':
					setColorScheme(preference);
					break;
				case 'system':
					handleSystemPreferenceChange();
					break;
			}
		},
		[setColorScheme, handleSystemPreferenceChange],
	);

	const currentPreference: ColorSchemePreference = useSystemPreference ? 'system' : activeColorScheme;

	return {
		colorScheme: activeColorScheme,
		preference: currentPreference,
		setColorSchemePreference,
	};
}
